import { Link } from '@purpur/library';
import styled, { css } from 'styled-components';

import {
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';
import {
    NAVIGATION_OFFSET_CSS_VARIABLE,
    NAVIGATION_OFFSET_TRANSITION_CSS_VARIABLE,
} from '@telialt/utils/src/NavigationUtils/constants';

export type ResponsiveVisibilityProps = {
    $target?: 'desktop' | 'mobile';
};

const desktopOnly = css`
    display: none;

    ${purpurMedia('lg')`
        display: flex;
    `}
`;

const mobileOnly = css`
    display: flex;

    ${purpurMedia('lg')`
        display: none;
    `}
`;

export const responsiveVisibility = ($target: 'desktop' | 'mobile') =>
    $target === 'desktop' ? desktopOnly : mobileOnly;

export const withResponsiveVisibilityMixin = css<ResponsiveVisibilityProps>`
    ${({ $target }) => ($target ? responsiveVisibility($target) : '')}
`;

export const ColumnsContainer = styled.div`
    max-width: 69.75rem;
    margin: 0 auto;
    padding-top: ${getPurpurSpacing('600')};
    padding-bottom: ${getPurpurSpacing('600')};
    gap: ${getPurpurSpacing('400')};

    ${purpurMedia('lg')`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding-top: ${getPurpurSpacing('800')};
        padding-bottom: ${getPurpurSpacing('1000')};
    `}
`;

const ARTICLE_PRODUCT_CARD_WIDTH = '19.75rem';
export const articleProductsGridClassName = 'article-products-grid';

export const ArticleProducts = styled.div`
    .${articleProductsGridClassName} {
        --padding: 0;
        padding-left: 0;
        padding-right: 0;
    }

    ${purpurMedia('lg')`
        .embla__slide {
            flex: 0 0 calc(${ARTICLE_PRODUCT_CARD_WIDTH} + ${getPurpurSpacing('300')});
            min-width: calc(${ARTICLE_PRODUCT_CARD_WIDTH} + ${getPurpurSpacing('300')});
        }
    `}
`;

export const ArticleContentColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('600')};

    ${purpurMedia('lg')`
        max-width: 34.125rem;
        flex-grow: 1;
    `}
`;

export const ArticleTopContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('300')};
`;

export const ArticleMetaColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('400')};
    padding-top: ${getPurpurSpacing('800')};

    ${purpurMedia('lg')`
        gap: ${getPurpurSpacing('600')};
        padding-top: 0;
        max-width: 27rem;
        flex-grow: 1;
    `}
`;

export const ArticleSummary = styled.div`
    font-size: ${getPurpurTypography('scale200')};

    ${purpurMedia('md')`
        font-size: ${getPurpurTypography('scale300')};
    `}

    ${purpurMedia('lg')`
        font-size: ${getPurpurTypography('scale400')};
    `}
`;

export const PublishDate = styled.div`
    color: ${getPurpurColor('textMedium')};
    font-size: ${getPurpurTypography('scale75')};
    line-height: ${getPurpurTypography('lineHeightLoose')};
`;

export const MetaWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('300')};
`;

export const StyledArticleTableOfContents = styled.div<ResponsiveVisibilityProps>`
    ${withResponsiveVisibilityMixin}

    ${({ $target }) =>
        $target === 'desktop' &&
        css`
            ${purpurMedia('lg')`
                position: sticky;
                top: calc(var(${NAVIGATION_OFFSET_CSS_VARIABLE}, 0rem) + ${getPurpurSpacing('400')});
                width: 100%;
                max-height: calc(100vh - var(${NAVIGATION_OFFSET_CSS_VARIABLE}, 0rem) - ${getPurpurSpacing('400')} * 2);
                overflow-y: auto;
                transition: var(${NAVIGATION_OFFSET_TRANSITION_CSS_VARIABLE});
            `}
        `}
`;

export const ArticleAuthors = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${getPurpurSpacing('200')};
`;

export const StyledArticleCategoryAndDate = styled.div<ResponsiveVisibilityProps>`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: ${getPurpurSpacing('200')};

    ${withResponsiveVisibilityMixin}
`;

export const CategoryLink = styled(Link).attrs({ variant: 'standalone' })`
    && a {
        font-size: ${getPurpurTypography('scale75')};
    }
`;

export const CategoriesList = styled.ul`
    list-style: none;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: ${getPurpurSpacing('200')};
    margin: 0;
    padding: 0;
    line-height: 1;
`;
